/** Collections: mine (create, open) and "Shared with me" (SPEC §22.4), on the shared design system. */
import { plural } from '@vw/core/src/plural';
import { useEffect, useState, type FormEvent } from 'react';
import { FolderHeart, Plus } from 'lucide-react';
import { Button, EmptyState, ErrorState, HeaderCard, Input, Spinner, StatChip, toast } from '@vw/ui';
import { api, type CollectionSummary } from '../api';
import { ago } from '../format';
import { Link, navigate } from '../router';
import { VIS_LABEL } from './Collection';

interface ListResponse {
  collections: CollectionSummary[];
  sharedWithMe: Array<{ id: string; name: string; emoji: string | null; itemCount: number; ownerName: string | null; updatedAt: number }>;
  limits: { collections: number | null; items: number };
}

export function Collections() {
  const [data, setData] = useState<ListResponse | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = () => { setError(null); api<ListResponse>('/api/collections').then(setData, (e) => setError(e.message)); };
  useEffect(load, []);

  async function create(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const c = await api<{ id: string }>('/api/collections', { method: 'POST', json: { name } });
      navigate(`/collections/${c.id}`);
    } catch (err) {
      toast.error('Could not create the collection', { description: (err as Error).message });
    } finally {
      setBusy(false);
    }
  }

  if (error) return <ErrorState message={`Could not load collections: ${error}`} onRetry={load} />;
  if (!data) return <Spinner label="Loading collections…" />;
  const atLimit = data.limits.collections !== null && data.collections.length >= data.limits.collections;
  return (
    <div className="mx-auto max-w-[1400px] space-y-6">
      <HeaderCard>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">Collections</h1>
            <p className="mt-1 text-sm text-muted-foreground">Group reels from any creators, note why they work, and share them.</p>
            <form className="mt-4 flex max-w-lg gap-2" onSubmit={create}>
              <Input className="flex-1" aria-label="Collection name" placeholder="e.g. Explainer videos" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} disabled={atLimit} />
              <Button type="submit" disabled={!name.trim() || atLimit} loading={busy}><Plus className="h-3.5 w-3.5" />Create</Button>
            </form>
            {data.limits.collections !== null && <p className="mt-2 text-xs text-muted-foreground">{data.collections.length} of {data.limits.collections} collections on your plan · up to {data.limits.items} reels each</p>}
          </div>
          <div className="flex gap-2"><StatChip value={data.collections.length} label="Yours" /><StatChip value={data.sharedWithMe.length} label="Shared with me" /></div>
        </div>
      </HeaderCard>

      <section className="space-y-3">
        <h2 className="text-sm font-bold">Yours</h2>
        {!data.collections.length ? (
          <EmptyState icon={<FolderHeart className="h-7 w-7" />} title="No collections yet">Create one above, or use "Add to collection" on any reel.</EmptyState>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {data.collections.map((c) => (
              <Link key={c.id} to={`/collections/${c.id}`} className="flex overflow-hidden rounded-2xl border border-border bg-card shadow-xs transition-shadow hover:shadow-md">
                {c.coverUrl ? <img className="h-28 w-20 shrink-0 object-cover" src={c.coverUrl} alt="" referrerPolicy="no-referrer" loading="lazy" /> : <span className="flex h-28 w-20 shrink-0 items-center justify-center bg-secondary text-3xl">{c.emoji ?? '📁'}</span>}
                <span className="min-w-0 space-y-0.5 p-3">
                  <span className="block truncate font-bold">{c.emoji ? `${c.emoji} ` : ''}{c.name}</span>
                  <span className="block text-xs text-muted-foreground">{plural(c.itemCount, 'reel')} · {VIS_LABEL[c.visibility]}{c.takedown ? ' · taken down' : ''}</span>
                  <span className="block text-xs text-muted-foreground">updated {ago(c.updatedAt)}</span>
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>

      {data.sharedWithMe.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-bold">Shared with me</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {data.sharedWithMe.map((c) => (
              <Link key={c.id} to={`/collections/${c.id}`} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4 shadow-xs hover:shadow-md">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-2xl">{c.emoji ?? '📁'}</span>
                <span className="min-w-0"><span className="block truncate font-bold">{c.name}</span><span className="block text-xs text-muted-foreground">{plural(c.itemCount, 'reel')} · from {c.ownerName ?? 'someone'}</span></span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
